import { useEffect, useState } from 'react';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { PageHeader, Pager, StatusBadge } from '@/components/kit';
import { FilterSelect, ListBody, SearchBox, Toolbar, useDebounced } from '@/components/inputs';
import DataTable, { RowLink } from '@/components/admin/DataTable';
import AnnouncementDialog, { LIVE_STYLES, SHOW_ON_LABELS, liveState } from '@/components/admin/AnnouncementDialog';
import { ANNOUNCEMENT_TONES, SHOW_ON, TONE_STYLES } from '@/shared/constants';
import { ANNOUNCEMENT_FIELDS } from '@/shared/fields';
import { paged } from '@/shared/data';
import { formatLocalDate, DATE_FORMATS } from '@/utils/date';

const { eq, contains } = window.ApperSDK.CoreSDK;

export const route = { path: '/admin/announcements', layout: 'owner', access: "['Admin_c','Staff_c'].includes(profile)" };
export const nav = { icon: 'BellRing', label: 'Announcements', section: 'Content', profiles: ['Admin_c', 'Staff_c'], order: 70 };

const PAGE_SIZE = 20;

export default function AdminAnnouncements() {
  const [tone, setTone] = useState('all');
  const [showOn, setShowOn] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const q = useDebounced(search.trim());

  useEffect(() => {
    setPage(1);
  }, [tone, showOn, q]);

  const { data, loading, error, run } = useFetch(() => {
    let query = sdk.table('announcements_c').select(ANNOUNCEMENT_FIELDS);
    if (tone !== 'all') query = query.where(eq('tone_c', tone));
    if (showOn !== 'all') query = query.where(eq('show_on_c', showOn));
    if (q) query = query.where(contains('Name', q));
    return query.orderByDesc('CreatedOn').page(page, PAGE_SIZE).fetch().then(paged);
  }, [tone, showOn, q, page]);

  const list = data?.rows ?? [];
  const filtered = tone !== 'all' || showOn !== 'all' || !!q;

  function clear() {
    setSearch('');
    setTone('all');
    setShowOn('all');
  }

  const columns = [
    {
      label: 'Message',
      render: (a) => <RowLink to={`/admin/announcements/${a.Id}`}>{a.Name}</RowLink>,
    },
    { label: 'Tone', render: (a) => <StatusBadge value={a.tone_c} styles={TONE_STYLES} /> },
    { label: 'Shows on', className: 'hidden md:table-cell', render: (a) => SHOW_ON_LABELS[a.show_on_c] ?? a.show_on_c },
    {
      label: 'Ends',
      className: 'hidden md:table-cell text-muted-foreground',
      render: (a) => (a.ends_at_c ? formatLocalDate(a.ends_at_c, DATE_FORMATS.WITH_TIME) : 'Until taken down'),
    },
    { label: 'State', render: (a) => <StatusBadge value={liveState(a)} styles={LIVE_STYLES} /> },
  ];

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        eyebrow="Content"
        title="Announcements"
        description="Banners across the top of the storefront and the customer account."
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <ApperIcon name="Plus" size={16} />
            New announcement
          </Button>
        }
      />

      <Toolbar>
        <SearchBox value={search} onChange={setSearch} placeholder="Search announcements…" />
        <FilterSelect value={tone} onChange={setTone} values={ANNOUNCEMENT_TONES} allLabel="All tones" />
        <FilterSelect value={showOn} onChange={setShowOn} values={SHOW_ON} labels={SHOW_ON_LABELS} allLabel="Anywhere" />
      </Toolbar>

      <ListBody
        loading={loading}
        data={data}
        error={error}
        run={run}
        isEmpty={!list.length}
        empty={{
          icon: 'BellRing',
          title: filtered ? 'No announcements match' : 'No announcements yet',
          description: filtered ? 'Nothing fits these filters.' : 'Post one before the next maintenance window.',
          action: filtered ? clear : () => setCreateOpen(true),
          actionLabel: filtered ? 'Clear filters' : 'New announcement',
        }}
      >
        <DataTable columns={columns} rows={list} />
        <Pager page={page} total={data?.total} pageSize={PAGE_SIZE} hasMore={data?.hasMore} onPage={setPage} />
      </ListBody>

      <AnnouncementDialog open={createOpen} onOpenChange={setCreateOpen} onSaved={run} />
    </div>
  );
}
